import AttachmentField from '../components/AttachmentField';
import { submitRequest } from '../lib/submitRequest';
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, Calendar, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import WhatsAppIcon from '../components/WhatsAppIcon';


export default function Contact() {
  const location = useLocation();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!submitted) return undefined;
    const timer = setTimeout(() => setSubmitted(false), 10000);
    return () => clearTimeout(timer);
  }, [submitted]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const formElement = e.currentTarget;
    setSending(true);
    setSubmitted(false);
    try {
      await submitRequest('contact', {
        subject: form.subject ? `Nouveau message MCE — ${form.subject}` : 'Nouveau message depuis le site MCE',
        type_de_demande: 'Message de contact',
        nom: form.name,
        email: form.email,
        entreprise: form.company || 'Non renseignée',
        message: form.message,
      }, formElement);
      setSubmitted(true);
      toast({ title: 'Message envoyé !', description: 'Merci, notre équipe vous répondra sous 24h.' });
      setForm({ name: '', email: '', company: '', subject: '', message: '' });
    } catch (error) {
      toast({ title: 'Envoi impossible', description: 'Votre message n’a pas pu être transmis. Réessayez ou contactez-nous sur WhatsApp.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };
  const params = new URLSearchParams(location.search);
  if (params.get('service') === 'formations' || params.has('formation') || location.hash === '#choix-formations') {
    return <Navigate replace to={`/talents-rh/academy${location.search}#formulaire-devis`} />;
  }
  if (location.hash === '#formulaire-devis' || params.has('service')) {
    return <Navigate replace to={`/solutions-digitales${location.search}#formulaire-devis`} />;
  }

  return (
    <div className="pt-[72px] contact-page">
      {/* CONTACT SECTION */}
      <section id="contact-form" className="py-12 md:py-16 scroll-mt-[72px]">
        <h1 className="text-center text-3xl md:text-4xl font-black text-mce-navy mb-10 px-6">Contactez-nous</h1>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left info */}
          <div className="lg:col-span-4 space-y-4">
            <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-mce-teal/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-mce-teal" />
                </div>
                <div>
                  <p className="font-bold text-[14px] text-mce-navy">Adresse</p>
                  <p className="text-[12.5px] text-mce-navy/70">9 rue des colonnes, 75002, Paris, France</p>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-6">
              <div>
                  <p className="font-bold text-[14px] text-mce-navy">Téléphone</p>
                  <div className="mt-2 flex flex-col gap-2">
                    <a href="tel:+33988195496" aria-label="Appeler le +33 9 88 19 54 96" className="flex items-center gap-3 text-[12.5px] text-mce-navy/70 hover:text-mce-teal">
                      <Phone aria-hidden="true" className="w-4 h-4 shrink-0 text-amber-600" />
                      <span>+33 9 88 19 54 96</span>
                    </a>
                    <a href="https://wa.me/33698681150" target="_blank" rel="noopener noreferrer" aria-label="Écrire sur WhatsApp au +33 6 98 68 11 50" className="contact-whatsapp-number flex items-center gap-3 text-[12.5px] text-mce-navy/70 hover:text-mce-teal">
                      <WhatsAppIcon className="w-4 h-4 shrink-0 text-green-600" />
                      <span>+33 6 98 68 11 50</span>
                    </a>
                  </div>
              </div>
            </div>

            <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-bold text-[14px] text-mce-navy">Email</p>
                  <a href="mailto:mceproagency@gmail.com" className="text-[12.5px] text-mce-navy/70 hover:text-mce-teal">mceproagency@gmail.com</a>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-white border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-bold text-[14px] text-mce-navy">Horaires</p>
                  <p className="text-[12.5px] text-mce-navy/70">Lun - Ven : 9h00 - 17h00</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-8">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-lg p-8">
              <h2 className="text-2xl font-black text-mce-navy">Envoyez-nous un message</h2>
              <p className="text-mce-navy/60 text-[13px] mt-1">Nous vous répondrons dans les plus brefs délais.</p>
              {submitted && (
                <div className="mt-5 flex items-center gap-2 bg-mce-teal/10 text-mce-teal p-3 rounded-lg text-[13px] font-semibold">
                  <CheckCircle2 className="w-4 h-4" />
                  Votre demande a bien été enregistrée.
                </div>
              )}
              <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <input type="checkbox" name="botcheck" tabIndex="-1" autoComplete="off" className="hidden" />
                <Field label="Nom complet *" name="name" value={form.name} onChange={onChange} required />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
                <Field label="Entreprise" name="company" value={form.company} onChange={onChange} />
                <Field label="Sujet" name="subject" value={form.subject} onChange={onChange} />
                <div className="md:col-span-2">
                  <label className="text-[12px] font-semibold text-mce-navy/80">Message *</label>
                  <textarea name="message" required value={form.message} onChange={onChange} rows={6} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-[14px] focus:border-mce-teal focus:outline-none focus:ring-2 focus:ring-mce-teal/30 transition-colors" placeholder="Décrivez votre projet..." />
                </div>

                <AttachmentField />

                {/* Lien Calendly — Appel découverte gratuit */}
                <div className="md:col-span-2 -mt-1">
                  <a
                    href="https://calendly.com/amane-mounia/coaching-decouverte-gratuit"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[13px] font-semibold text-mce-teal hover:text-mce-navy transition-colors group"
                  >
                    <Calendar className="w-4 h-4 shrink-0" />
                    <span className="underline underline-offset-4 decoration-mce-teal/40 group-hover:decoration-mce-navy">
                      Réserver votre appel
                    </span>
                  </a>
                </div>

                <div className="md:col-span-2 flex justify-end">
                  <button type="submit" disabled={sending} className="btn-gold disabled:cursor-wait disabled:opacity-60">
                    <Send className="w-4 h-4" />
                    {sending ? 'ENVOI EN COURS…' : 'ENVOYER LE MESSAGE'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

function Field({ label, name, type='text', value, onChange, required }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-mce-navy/80">{label}</label>
      <input type={type} inputMode={type==='tel'?'tel':undefined} name={name} required={required} value={value} onChange={onChange} className={`mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-[14px] focus:border-mce-teal focus:outline-none focus:ring-2 focus:ring-mce-teal/30 transition-colors ${type==='tel'?'bg-white':''}`} />
    </div>
  );
}
